import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  styled,
  Typography,
} from '@mui/material';
import { t as trans } from 'utils/i18n';
import warningIcon from '/static/svg/ic_alert_yellow.svg';
import { useRef, useState } from 'react';
import { Document, Page, pdfjs, DocumentProps } from 'react-pdf';
import { useResponsive } from '@onedesign/ui';
import PolicyPrivacyFile from 'assets/documents/privacyPolicyDocument.pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
type LoadCallback = Required<DocumentProps>['onLoadSuccess'];

export type TPDPADocument = {
  openDocument: boolean;
  closeDocument: (val: boolean) => void;
  confirmDocument: () => void;
};

const FlexContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const WarningContainer = styled(FlexContainer)({
  padding: '8px 12px',
  background: '#FFF7CD',
  borderRadius: '8px',
});

export const PdfContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  justifyContent: 'center',
  borderRadius: '16px',
  marginTop: '20px',
  border: `1.5px solid ${theme.palette.grey[400]}`,
  padding: '25px 0',
}));

export const PreviewPdfWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  height: '40vh',
  backgroundColor: '#817D7D',
  overflow: 'auto',
}));

const AgreementCheckBox = styled(FormControlLabel)({
  position: 'absolute',
  left: '0',
  bottom: '0',
  width: '100%',
  margin: 0,
  display: 'flex',
  justifyContent: 'center',
  borderBottomLeftRadius: '14px',
  borderBottomRightRadius: '14px',
});

export const PolicyPrivacyDocument = ({
  openDocument,
  closeDocument,
  confirmDocument,
}: TPDPADocument) => {
  const isDesktop = useResponsive('up', 'sm');
  const isMediumScreen = useResponsive('up', 'md');
  const [numPages, setNumPages] = useState<number>(0);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isShowCheckbox, setIsShowCheckbox] = useState<boolean>(false);
  const prdContainerRef = useRef<HTMLDivElement | null>(null);
  const SCROLL_EPS = 20;

  const handleScroll = () => {
    const element = prdContainerRef.current;
    if (element) {
      const isAtBottom =
        Math.abs(
          element.scrollHeight - element.scrollTop - element.clientHeight,
        ) < SCROLL_EPS;
      if (isAtBottom) {
        setIsShowCheckbox(true);
      }
    }
  };

  const onCloseDocument = () => {
    setIsShowCheckbox(false);
    setIsChecked(false);
    closeDocument(!openDocument);
  };

  const onConfirmDocument = () => {
    confirmDocument();
    onCloseDocument();
  };

  const onDocumentLoad: LoadCallback = pdf => {
    setNumPages(pdf.numPages);
  };

  const setDocumentPageWidth = (): number => {
    if (isDesktop && isMediumScreen) return 850;
    if (!isDesktop) return 330;
    if (isDesktop) return 600;
    return 0;
  };

  return (
    <Dialog
      open={openDocument}
      disableEscapeKeyDown
      maxWidth={isMediumScreen ? 'md' : 'sm'}
      fullWidth={true}
    >
      <DialogTitle sx={{ marginBottom: '20px' }}>
        {trans('register.privacyPolicyDocument.title', '個人資料使用同意書')}
      </DialogTitle>
      <DialogContent>
        <WarningContainer>
          <img src={warningIcon} alt="Warning icon" />
          <Typography
            sx={{
              color: '#B7640E',
              fontWeight: 500,
            }}
          >
            {trans(
              'register.privacyPolicyDocument.warningMessage',
              '此為重要閱讀事項，下拉查看完整內容後點選同意按鈕進行下一步',
            )}
          </Typography>
        </WarningContainer>
        <PdfContainer>
          <PreviewPdfWrapper ref={prdContainerRef} onScroll={handleScroll}>
            <Document file={PolicyPrivacyFile} onLoadSuccess={onDocumentLoad}>
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  width={setDocumentPageWidth()}
                />
              ))}
            </Document>
          </PreviewPdfWrapper>
          <AgreementCheckBox
            sx={
              isChecked
                ? { background: '#FFA21C', color: 'white' }
                : { background: '#FFF3D1' }
            }
            disabled={!isShowCheckbox}
            label={trans(
              'register.privacyPolicyDocument.agreementCheckbox',
              '我已同意並閱讀此隱私權聲明',
            )}
            control={
              <Checkbox
                sx={
                  isChecked
                    ? {
                        '&.Mui-checked': {
                          color: 'white',
                        },
                      }
                    : {}
                }
              />
            }
            onChange={() => {
              if (!isShowCheckbox) return;
              setIsChecked(pre => !pre);
            }}
          />
        </PdfContainer>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
        }}
      >
        <Button onClick={onCloseDocument} variant="outlined" color="inherit">
          {trans('register.privacyPolicyDocument.cancelButton', '取消')}
        </Button>
        <Button
          onClick={onConfirmDocument}
          color="primary"
          variant="contained"
          disabled={!isChecked}
        >
          {trans('register.privacyPolicyDocument.confirmButton', '確認')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
